<template>
    <b-overlay
        class="h-100 position-relative"
        :show="status === 'loading' && !editMode"
    >
        <div class="p-2 h-100">
            <div class="card-text pb-1">
                <div class="d-flex justify-content-between">
                    <h4 class="card-title">
                        {{ $t('org.dashboard.Download invoice') }}
                    </h4>
                    <b-dropdown
                        v-if="editMode"
                        text="2024-05"
                        variant="flat-primary"
                        style="background-color: rgba(115, 103, 240, 0.12)!important;"
                    />
                    <b-dropdown
                        v-else
                        id="invoice-month"
                        :text="invoiceSelectedMonth"
                        variant="flat-primary"
                        style="background-color: rgba(115, 103, 240, 0.12)!important;"
                    >
                        <b-dropdown-item
                            v-for="(month, index) in monthOptions"
                            :key="index"
                            @click="handleSelectMonth(month)"
                        >
                            {{ month }}
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
                <h6 class="card-subtitle text-muted mb-2 font-small-4">
                    {{ $t('org.dashboard.Total Cost') }} {{ $_getDollar(cost) }}
                </h6>
            </div>
            <div class="d-flex justify-content-between pt-2 centered-content-download">
                <div
                    class="flex-grow-1 d-flex align-items-start"
                    :class="disabledButton ? 'cursor-not-allowed' : 'cursor-pointer pointer-event'"
                    @click="handleDownloadExcel"
                >
                    <div
                        class="align-self-start mr-75"
                    >
                        <b-button
                            class="btn-circle btn-small"
                            :class="{...disabledButtonClass, ...responsiveCircleButton}"
                            :disabled="disabledButton"
                            variant="info"
                        >
                            <i v-if="excelLoading" class="fas fa-spinner fa-spin fa-fw fa-1x"></i>
                            <i v-else class="fas fa-file-excel fa-fw fa-1x" style="transform: scale(1.17)"></i>
                        </b-button>
                    </div>
                    <div class="align-self-start">
                        <small
                            class="mb-0 font-small-4"
                            :class="{ 'text-disabled': disabledButton, 'text-info': needResponsiveStyle }"
                        >
                            {{ $t('org.dashboard.XLSX of Usage Summary') }}
                        </small>
                        <div
                        class="card-text font-small-3 mb-0"
                        :class="cardTextClass"
                        >
                            Xlsx
                        </div>
                    </div>
                </div>
                <div
                    class="flex-grow-1 d-flex align-items-start"
                    :class="disabledButton ? 'cursor-not-allowed' : 'cursor-pointer pointer-event'"
                    @click="handleDownloadCSV"
                >
                    <div
                        class="align-self-start mr-75"
                    >
                        <b-button
                            class="btn-circle btn-small"
                            :class="{...disabledButtonClass, ...responsiveCircleButton}"
                            :disabled="disabledButton"
                            variant="success"
                        >
                            <i v-if="csvLoading" class="fas fa-spinner fa-spin fa-fw fa-1x"></i>
                            <i v-else class="fas fa-file-csv fa-fw fa-1x" style="transform: scale(1.17)"></i>
                        </b-button>
                    </div>
                    <div class="align-self-start">
                        <small
                            class="mb-0 font-small-4"
                            :class="{ 'text-disabled': disabledButton, 'text-success': needResponsiveStyle }"
                        >
                            {{ $t('org.dashboard.CSV of Usage Details') }}
                        </small>
                        <div
                            class="card-text font-small-3 mb-0"
                            :class="cardTextClass"
                        >
                            CSV
                        </div>
                    </div>
                </div>
                <div
                    class="flex-grow-1 d-flex align-items-start"
                    :class="hasInvoice ? 'cursor-pointer pointer-event' : 'cursor-not-allowed'"
                    @click="handleDownloadPDF"
                >
                    <div
                        class="align-self-start mr-75"
                    >
                        <b-button
                            class="btn-circle btn-small"
                            :class="pdfClasses"
                            :disabled="editMode ? false : hasInvoice ? disabledButton : 'disabled'"
                            variant="danger"
                        >
                            <i v-if="pdfLoading" class="fas fa-spinner fa-spin fa-fw fa-1x"></i>
                            <i v-else class="fas fa-file-pdf fa-fw fa-1x" style="transform: scale(1.17)"></i>
                        </b-button>
                    </div>
                    <div class="align-self-start">
                        <small
                            class="mb-0 font-small-4"
                            :class="{ 'text-disabled': (!editMode && (!hasInvoice || disabledButton)), 'text-danger': needResponsiveStyle }"
                        >
                            {{ $t('org.dashboard.PDF of Usage Statement') }}
                        </small>
                        <div
                            class="card-text font-small-3 mb-0"
                            :class="editMode ? false : hasInvoice ? cardTextClass : 'text-disabled'"
                        >
                            PDF
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-overlay>
</template>
<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import { getCurrentInstance, computed, watch, ref } from '@vue/composition-api'
import useSelectProjectAndGroup from '@/composables/useSelectProjectAndGroup'
import useSelectMonth from '@/composables/useSelectMonth'
import useDashboard from '@/composables/useDashboard'
import useSummaryDownload from '@/composables/useSummaryDownload'
import useDashboardRender from '@/composables/useDashboardRender'
export default {
    props: ['editMode'],
    setup () {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const { monthOptions, selectedMonth } = useSelectMonth()
        const { selectedProject, qtypeParam } = useSelectProjectAndGroup()
        const {
            getCostByMonth
        } = useDashboard()
        const {
            downloadInvoicePDF,
            downloadDetailCSV,
            downloadSimpleExcel,
            getAvailableInvoice
        } = useSummaryDownload()
        const {
            windowWidth
        } = useDashboardRender()
        const needResponsiveStyle = computed(() => (windowWidth.value > 992 && windowWidth.value < 1440) || (windowWidth.value < 550))
        const locale = computed(() => vm.$i18n.locale)
        const invoiceSelectedMonth = ref(selectedMonth.value)
        const currentParams = computed(() => store.getters['$_dashboard/currentParams'])
        const [status, cost] = getCostByMonth(currentParams.value)
        const isNoContent = computed(() => status.value === 204)
        const [pdfStatus, pdfLoading] = downloadInvoicePDF()
        const [csvStatus, csvLoading] = downloadDetailCSV()
        const [excelStatus, excelLoading] = downloadSimpleExcel()
        const [hasInvoiceStatus, hasInvoice] = getAvailableInvoice()
        if (qtypeParam.value.qtype === 'project') {
            getAvailableInvoice({
                usageAccountId: selectedProject.value.usageAccountId,
                month: selectedMonth.value
            })
        }
        const disabledButton = computed(() => !selectedProject.value || status.value === 204 ? 'disabled' : null)
        const disabledButtonClass = computed(() => ({
            'cursor-not-allowed': disabledButton.value === 'disabled'
        }))
        const responsiveCircleButton = computed(() => ({
            'd-none': needResponsiveStyle.value
        }))
        const cardTextClass = computed(() => ({
            'text-disabled': disabledButton.value
        }))
        const handleDownloadPDF = () => {
            if (!selectedProject.value?.usageAccountId || !hasInvoice.value || isNoContent.value) return
            downloadInvoicePDF({
                usageAccountId: selectedProject.value.usageAccountId,
                month: invoiceSelectedMonth.value,
                locale: locale.value
            })[2].then((data) => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', 'invoice.pdf')
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }
        const handleDownloadCSV = () => {
            if (disabledButton.value || isNoContent.value) return
            downloadDetailCSV({
                usageAccountId: selectedProject.value.usageAccountId,
                month: invoiceSelectedMonth.value
            })[2].then(() => {
                vm.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: vm.$t('org.message.Creating CSV file'),
                        icon: 'CoffeeIcon',
                        variant: 'success'
                    }
                })
            })
        }
        const pdfClasses = computed(() => {
            return {
                'cursor-not-allowed': !hasInvoice.value || isNoContent.value,
                'd-none': needResponsiveStyle.value
            }
        })
        const handleDownloadExcel = () => {
            if (disabledButton.value || isNoContent.value) return
            downloadSimpleExcel({
                usageAccountId: selectedProject.value.usageAccountId,
                month: invoiceSelectedMonth.value
            })[2].then(data => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute('download', `${selectedProject.value.usageAccountId}-AWS-Simple.xlsx`)
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }
        const fetchData = () => {
            if (selectedProject.value?.usageAccountId) {
                getAvailableInvoice({
                    usageAccountId: selectedProject.value.usageAccountId,
                    month: invoiceSelectedMonth.value
                })
            }
            getCostByMonth(currentParams.value, {
                ...currentParams.value,
                month: invoiceSelectedMonth.value
            })
        }

        const handleSelectMonth = (value) => {
            invoiceSelectedMonth.value = value
            fetchData()
        }
        watch(currentParams, (value, oldValue) => {
            invoiceSelectedMonth.value = selectedMonth.value
            fetchData()
        }, {
            deep: true
        })
        return {
            status,
            cost,
            pdfClasses,
            pdfStatus,
            pdfLoading,
            csvStatus,
            csvLoading,
            excelStatus,
            excelLoading,
            hasInvoiceStatus,
            hasInvoice,
            monthOptions,
            invoiceSelectedMonth,
            handleSelectMonth,
            handleDownloadPDF,
            handleDownloadCSV,
            handleDownloadExcel,
            disabledButton,
            disabledButtonClass,
            cardTextClass,
            needResponsiveStyle,
            responsiveCircleButton,
            isNoContent
        }
    }
}
</script>
<style lang="scss" scoped>
.btn-circle {
    width: 42px;
    height: 42px;
    border-radius: 50% !important;
    padding: 6px;
}
.btn-circle-smaller {
    width: 37px;
    height: 37px;
}
.btn[disabled] {
    opacity: 0.25 !important;
}
.text-disabled {
    color: #B4B4B4 !important;
}
.centered-content-download {
    position: absolute;
    width: 97%;
    top: 50%;
    transform: translateY(calc(-50% + 20px));
}
</style>
